// The API token the library sends straight to the application server.
//
// Distinct from the app token, which only ever proves to a netlify function
// that the platform password was entered. This one is minted by the server and
// is what /api actually accepts, which is how a call can run past the 30
// seconds a netlify function is allowed.

import * as locCon from '../LocalConstants';
import { isNullOrUndefined } from '../community-hats-js-library/utils/generalFunctions.js';
import { getStoredValue, setStoredValue } from '../hooks/GeneralStoreHooks';

const TOKEN_ENDPOINT = '/.netlify/functions/getApiToken';

// Renew this far ahead of expiry, so a call started just under the wire cannot
// have its token die underneath it. Comfortably longer than the slowest
// function on the server.
const RENEW_BEFORE_MS = 30 * 60 * 1000;

// One in flight request at a time. Without this, the several dataset loads
// that fire together at log in would each mint a token and the last to return
// would win, which works but spends the server secret once per call.
let inFlight = null;

const isUsable = () => {
  const token = getStoredValue(locCon.API_TOKEN);
  const expiry = getStoredValue(locCon.API_TOKEN_EXPIRY);

  if (isNullOrUndefined(token) || isNullOrUndefined(expiry)) return false;

  return Number(expiry) - Date.now() > RENEW_BEFORE_MS;
};

const mint = async () => {
  // The netlify function refuses this without a valid app token, so there is
  // nothing to try when the user is not logged in.
  const appToken = getStoredValue(locCon.APP_TOKEN);

  if (isNullOrUndefined(appToken)) return null;

  try {
    const response = await fetch(TOKEN_ENDPOINT, {
      method: 'POST',
      headers: { 'x-app-token': appToken },
    });

    if (!response.ok) {
      console.log(`Could not mint an API token (status ${response.status}).`);
      return null;
    }

    const { token, expiresAt } = await response.json();

    setStoredValue(locCon.API_TOKEN, token);
    setStoredValue(locCon.API_TOKEN_EXPIRY, expiresAt);

    return token;
  } catch (error) {
    console.log(`Could not mint an API token: ${error.message}`);
    return null;
  }
};

/**
 * The current API token, minting one first if there is none or it is close to
 * expiring. This is what the library's router is handed at start up, so every
 * back-end call goes through here.
 *
 * @returns {Promise<string | null>} null when no token could be obtained, which
 *                                   the router reports as a transport failure.
 */
export const getApiToken = async () => {
  if (isUsable()) return getStoredValue(locCon.API_TOKEN);

  // Reuse the in flight mint rather than starting a second one.
  if (inFlight === null)
    inFlight = mint().finally(() => {
      inFlight = null;
    });

  return inFlight;
};

/**
 * Drops the stored token. Called at log in, so that a new session never
 * inherits the previous one's token.
 */
export const clearApiToken = () => {
  setStoredValue(locCon.API_TOKEN, null);
  setStoredValue(locCon.API_TOKEN_EXPIRY, null);
};
